/*
 * Copyright (c) 2026. Nedaware SL
 */

import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {ManagerCachingStrategy, PropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {DireccionPersona} from "./direccion-persona.model";
import {TelefonoPersona} from "./telefono-persona.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {IRI_PROPERTY, TYPE_PROPERTY} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {Persona} from "./persona.model";

/**
 * Clínica con identidad propia que cuelga de un cliente con permiso para gestionar asociados
 * (`Cliente.esPadre`). No entra en el sistema y no se le factura: la factura sale siempre al
 * cliente padre, aunque el pedido se entregue en la dirección de la clínica.
 *
 * Extiende `Persona` (no `Resource` directo), igual que hace la API: `ClienteAsociado extends
 * PersonaSuperclass` en el backend. `DireccionPersona.persona`/`TelefonoPersona.persona` se
 * declaran como `PropertyType.Uri(Persona)`, y `InterfaceProviderService.allAcceptedInterfaces()`
 * calcula los tipos aceptados recorriendo la cadena de prototipos (`isInstanceOf`), no por una
 * lista explícita. Con `extends Resource`, `ClienteAsociado` quedaba fuera de esa cadena: al
 * deserializar cualquier `DireccionPersona` cuyo propietario fuera una clínica -exactamente lo que
 * hace el precache de este mismo modelo, `ManagerCachingStrategy.PrecacheOnStart`, en cuanto exista
 * una fila real- `UriCaster.toModel()` no encuentra su `@type` entre los tipos aceptados y lanza una
 * excepción. Mismo fallo, mismo arreglo, que en cnc-client (ver el desplegable de dirección de
 * entrega de "Nuevo pedido" en ese proyecto). Verificado en vivo aquí también, sin pasar por el
 * precache: `InterfaceProviderService.allAcceptedInterfaces(Persona)` ya incluye `ClienteAsociado`,
 * y deserializar por API una `DireccionPersona` real cuyo propietario es un `ClienteAsociado` -antes
 * tiraba `UriCaster for uriPersona: Invalid uri object with @type ClienteAsociado`- ya no revienta.
 *
 * `Persona` no obliga a ningún método nuevo (sólo campos con getter/setter) y el deserializador
 * ignora en silencio cualquier propiedad ausente en la respuesta de la API
 * (`BasicEntitySerialiser.fromApi()`: `if (value !== undefined)` antes de castear), así que heredar
 * los campos de `Persona` que la API no expone para `ClienteAsociado` (nombre, apellidos, fechas,
 * usuario, idioma...) no rompe nada.
 */
@BasicEntity({
    name: 'Clientes asociados',
    endpoint: 'cliente_asociados',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'denominacionSocial',
    acceptsBulkIriSearch: true,
    type: 'ClienteAsociado',
    managerCaching: ManagerCachingStrategy.NoCache
})
export class ClienteAsociado extends Persona {

    // Sin campo ni get/set propios para 'id': hereda los de Persona (idéntico
    // PropertyType.Integer()).

    // Circular con cliente.model.ts (Cliente referencia ClienteAsociado y viceversa): se resuelve
    // con FutureUri aquí y el parcheo de este mapa al final de cliente.model.ts, igual que
    // DescuentoCliente.MAP_CLIENTE.
    public static readonly CLIENTE_MAP: PropertyMap = {
        name: 'Cliente',
        type: PropertyType.FutureUri('cliente.model.ts')
    };
    @BasicProperty(ClienteAsociado.CLIENTE_MAP)
    private _cliente;

    @BasicProperty({
        name: 'Nombre de la clínica',
        type: PropertyType.String(),
        order: 0
    })
    private _denominacionSocial: string;

    // Sin campo ni get/set propios para 'cifNif'/'email': heredados de Persona (mismo
    // PropertyType.String() para cifNif; PropertyType.Email() para email, que castea igual que
    // String() -sin caster propio, ver property-type.ts- así que no cambia el dato que se envía ni
    // se lee). La API no expone ninguno de los dos para ClienteAsociado hoy -no es cosa de este
    // cambio-, y el deserializador ignora en silencio la clave ausente.

    @BasicProperty({
        name: 'Baja',
        type: PropertyType.Boolean(),
        order: 3
    })
    private _baja: boolean = false;

    // Escribible anidada, igual que Persona.direcciones: el alta/edición del asociado manda
    // siempre la colección completa (nunca un PUT parcial que la omita), que es lo que contiene el
    // riesgo del orphanRemoval de PersonaSuperclass::$direcciones. Se excluye 'personaId' del
    // subformulario: es un campo de sólo filtrado en la API (persona.id), nunca de escritura, y
    // dejarlo visible sólo confundiría.
    //
    // Sin 'tag' a propósito: esta propiedad no se expone directamente en ningún formulario (igual
    // que Persona._direcciones para Cliente, que tampoco aparece en su ficha). Trae siempre la
    // colección completa -históricas incluidas, que DireccionPersonaSubscriber genera al editar una
    // dirección para no alterar pedidos ya emitidos-. Lo único que ve y edita el usuario es
    // 'direccionesVigentes' más abajo. Que 'columns' se calcule a partir del mapa completo de la
    // API implica que no basta con no tener tag: clientes-edition.component.ts también tiene que
    // excluir 'direcciones' al construir las 'columns' del diálogo del asociado (alta y edición),
    // o volvería a aparecer como campo suelto.
    // `protected` en el campo (aquí y en Persona): redeclara con exclusiones distintas
    // ([IRI_PROPERTY, TYPE_PROPERTY, 'personaId']) las mismas que ya traía antes de heredar de
    // Persona; con `private` en Persona, TypeScript no deja redeclararla en la subclase.
    @BasicProperty({
        name: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY, 'personaId']),
        array: true
    })
    protected _direcciones = [];

    // Propiedad derivada, sin dato propio: es sólo un ancla para el decorador (igual que
    // Cliente._direccionFacturacion/_direccionEnvio), la lógica real vive en el getter/setter
    // 'direccionesVigentes' de más abajo.
    @BasicProperty({
        name: 'Direcciones',
        tag: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY, 'personaId']),
        array: true,
        dontReadFromApi: true
    })
    private _direccionesVigentes;

    // A diferencia de direcciones, sí es escribible anidado: el asociado no ofrece picking de
    // teléfonos existentes (CreateOnly) y no arrastra el mismo riesgo de borrado en cascada.
    // `protected` en el campo (aquí y en Persona): redeclara para no llevar 'order' -irrelevante
    // aquí-, y con `private` en Persona no se puede redeclarar en la subclase.
    @BasicProperty({
        name: 'Teléfonos',
        tag: 'Teléfonos',
        type: PropertyType.NestedModel(TelefonoPersona, 'persona', NestedBehavior.CreateOnly),
        array: true
    })
    protected _telefonos;

    get cliente() {
        return this._cliente;
    }

    set cliente(value) {
        this._cliente = value;
    }

    get denominacionSocial() {
        return this._denominacionSocial;
    }

    set denominacionSocial(value) {
        this._denominacionSocial = value;
    }

    // Sin get/set propios para 'cifNif'/'email': ver el comentario junto a sus campos, más arriba.

    get baja() {
        return this._baja;
    }

    set baja(value) {
        this._baja = value;
    }

    get direcciones() {
        return this._direcciones;
    }

    set direcciones(value) {
        this._direcciones = value;
    }

    /**
     * Sólo las direcciones vigentes (no históricas) de 'direcciones'. Es lo único que el formulario
     * llega a ver: lo que el usuario añade, edita o borra aquí sólo toca este subconjunto.
     */
    get direccionesVigentes() {
        return (this.direcciones || []).filter(d => d && !d.historico);
    }

    /**
     * Reconstruye 'direcciones' conservando las históricas tal cual estaban -nunca se editan ni se
     * borran, siguen referenciadas por pedidos ya emitidos- y sustituyendo el bloque de vigentes por
     * lo que haya dejado el formulario. Es 'direcciones' (la colección completa), no esto, lo que
     * viaja a la API al guardar.
     */
    set direccionesVigentes(value) {
        this._direccionesVigentes = value;
        this._direcciones = [
            ...(this._direcciones || []).filter(d => d && d.historico),
            ...(value || [])
        ];
    }

    get telefonos() {
        return this._telefonos;
    }

    set telefonos(value) {
        this._telefonos = value;
    }
}
